import { canAccessNutrable } from './utils/nutrable';
import { createRouter, createWebHistory } from 'vue-router';
import axios from 'axios';
import UcariHome from './components/UcariHome.vue';
import TestKits from './components/TestKits.vue';
import RegisterKit from './components/RegisterKit.vue';
import RegisterKitSuccessful from './components/RegisterKitSuccessful.vue';
import TestResults from './components/TestResults.vue';
import TestResultsV1 from './components/TestResultsV1.vue';
import AllTestItems from './components/AllTestItems.vue';
import RegisterUser from './components/RegisterUser.vue';
import RegisterUserSuccessful from './components/RegisterUserSuccessful.vue';
import LoginUser from './components/LoginUser.vue';
import VerifyEmail from './components/VerifyEmail.vue';
import EmailVerified from '@/components/EmailVerified.vue';
import RequestPasswordReset from './components/RequestPasswordReset.vue';
import RequestPasswordResetSuccessful from './components/RequestPasswordResetSuccessful.vue';
import ResetPassword from './components/ResetPassword.vue';
import ResetPasswordSuccessful from './components/ResetPasswordSuccessful.vue';
import UserProfile from './components/UserProfile.vue';
import Billing from './components/Billing.vue';
import PetVetConnection from './components/PetVetConnection.vue';
import IngredientChecker from './components/IngredientChecker.vue';
import IngredientCheckerHistory from './components/IngredientCheckerHistory.vue';
import IngredientCheckerSubscription from './components/IngredientCheckerSubscription.vue';
import NutrableProgram from './components/NutrableProgram.vue';
import NotFound from './components/NotFound.vue';
import { isIngredientCheckerEnabledForCid, shouldHideIngredientChecker } from './utils/common';

const routes = [
  {
    path: '/welcome',
    name: 'UcariHome',
    component: UcariHome,
    meta: { requiresAuth: false }
  },
  {
    path: '/',
    name: 'TestKits',
    component: TestKits,
    meta: { requiresAuth: true }
  },
  {
    path: '/register',
    name: 'RegisterUser',
    component: RegisterUser,
    meta: { requiresAuth: false }
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: VerifyEmail,
    meta: { requiresAuth: false }
  },
  {
    path: '/email-verified',
    name: 'EmailVerified',
    component: EmailVerified,
    meta: { requiresAuth: false }
  },
  {
    path: '/registration-successful',
    name: 'RegisterUserSuccessful',
    component: RegisterUserSuccessful,
    meta: { requiresAuth: false }
  },
  {
    path: '/login',
    name: 'LoginUser',
    meta: { requiresAuth: false },
    component: LoginUser
  },
  {
    path: '/request-password-reset',
    name: 'RequestPasswordReset',
    component: RequestPasswordReset,
    meta: { requiresAuth: false }
  },
  {
    path: '/request-password-reset-successful',
    name: 'RequestPasswordResetSuccessful',
    component: RequestPasswordResetSuccessful,
    meta: { requiresAuth: false }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { requiresAuth: false }
  },
  {
    path: '/reset-password-successful',
    name: 'ResetPasswordSuccessful',
    component: ResetPasswordSuccessful,
    meta: { requiresAuth: false }
  },
  {
    path: '/test-results',
    name: 'TestResults',
    component: TestResults,
    meta: { requiresAuth: true }
  },
  {
    path: '/ingredient-checker',
    name: 'IngredientChecker',
    component: IngredientChecker,
    meta: { requiresAuth: true }
  },
  {
    path: '/ingredient-checker-history',
    name: 'IngredientCheckerHistory',
    component: IngredientCheckerHistory,
    meta: { requiresAuth: true }
  },
  {
    path: '/ingredient-checker-subscription',
    name: 'IngredientCheckerSubscription',
    component: IngredientCheckerSubscription,
    meta: { requiresAuth: true }
  },
  {
    path: '/12-week-nutrition-program',
    name: 'NutrableProgram',
    component: NutrableProgram,
    meta: { requiresAuth: true }
  },
  {
    path: '/test-results-v1',
    name: 'TestResultsV1',
    component: TestResultsV1,
    meta: { requiresAuth: true }
  },
  {
    path: '/all-test-items',
    name: 'AllTestItems',
    component: AllTestItems,
    meta: { requiresAuth: true }
  },
  {
    path: '/register-kit',
    name: 'RegisterKit',
    component: RegisterKit,
    meta: { requiresAuth: true }
  },
  {
    path: '/kit-registered',
    name: 'RegisterKitSuccessful',
    component: RegisterKitSuccessful,
    meta: { requiresAuth: true }
  },
  {
    path: '/profile',
    name: 'UserProfile',
    component: UserProfile,
    meta: { requiresAuth: true }
  },
  {
    path: '/billing',
    name: 'Billing',
    component: Billing,
    meta: { requiresAuth: true }
  },
  {
    path: '/billing/success',
    name: 'BillingSuccess',
    component: Billing,
    meta: { requiresAuth: true }
  },
  {
    path: '/billing/cancel',
    name: 'BillingCancel',
    component: Billing,
    meta: { requiresAuth: true }
  },
  {
    path: '/pet-vet-connection',
    name: 'PetVetConnection',
    component: PetVetConnection,
    meta: { requiresAuth: true }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound
  }  
];

const router = createRouter({
  history: createWebHistory(),
  mode: 'history',
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.hash) return { el: to.hash, behavior: 'smooth' };
    return { top: 0, left: 0 };
  }
});

router.beforeEach(async (to, from, next) => {
  getQueryParams();

  if (['/ingredient-checker', '/ingredient-checker-history'].includes(to.path)) {
    const cid = localStorage.getItem('cid');
    if (shouldHideIngredientChecker(cid)) {
      next({ path: '/' });
      return;
    }
  }

  if (to.path === '/ingredient-checker-subscription') {
    const cid = localStorage.getItem('cid');
    if (!isIngredientCheckerEnabledForCid(cid)) {
      next({ path: '/ingredient-checker' });
      return;
    }
  }

  if (to.meta.requiresAuth) {
    const token = localStorage.getItem('accessToken');
    const cid = localStorage.getItem('cid');
    if (token && cid) {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/check-auth`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'cid': cid
          }
        });

        if (response.data.authenticated) {
          if (to.name === 'NutrableProgram' && !await canAccessNutrable()) {
            next({ path: '/' });
            return;
          }
          next();
        } else {
          next({ path: '/login', query: { redirect: to.fullPath } });
        }
      } catch (error) {
        next({ path: '/login', query: { redirect: to.fullPath } });
      }
    } else {
      next({ path: '/login', query: { redirect: to.fullPath } });
    }
  } else {
    next();
  }
});

function getQueryParams() {
  const params = new URLSearchParams(window.location.search);
  const accessToken = params.get('t');
  const customerGuid = params.get('c');

  if (accessToken && customerGuid) {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('cid', customerGuid);

    window.history.replaceState({}, document.title, '/');
  }
}
export default router;
